import { useRef, useState } from 'react'

import { Box, Table, colors } from '@factorialco/deprecated-design-system'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import useSubscribe from 'modules/core/hooks/useSubscribe'
import i18n from 'modules/core/lib/i18n'

import { useFilterProvider } from 'modules/finance/components/FinancialWorkspace/Providers/FiltersProvider'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import {
  REFETCH_DATA_EVENT,
  resolveItemsPerPage,
} from 'modules/finance/components/FinancialWorkspace/components/Table/const'
import { EmptyStateProps } from 'modules/finance/components/FinancialWorkspace/components/Table/types'
import { useCurrentPage } from 'modules/finance/components/FinancialWorkspace/hooks/useCurrentPage'
import { useTrackReportRun } from 'modules/finance/components/FinancialWorkspace/hooks/useTrackReportRun'
import {
  InsightTableGroupBy,
  InsightTableType,
  TableDataVariables,
  TableProps,
} from 'modules/finance/components/FinancialWorkspace/types/table'

import { EmptyStateTable } from './EmptyState'
import { Header } from './Header'
import { Loading } from './Loading'
import { NestedRows } from './NestedRows'
import { LoadingRow } from './Row/LoadingRow'
import { Ungrouped } from './Row/Ungrouped'
import { useVisitTracking } from './hooks/useVisitTracking'

const isNumberArray = (arr: EmployeeForSelectFragmentNoAccess[] | number[]): arr is number[] =>
  typeof arr[0] === 'number'

const getEmployeeIds = (
  employees: EmployeeForSelectFragmentNoAccess[] | number[] | undefined
): number[] | undefined => {
  if (!employees || employees.length === 0) return undefined
  return isNumberArray(employees) ? employees : employees.map((emp) => emp.id)
}

interface PaginatedTableProps {
  title: string
  search: string
  type?: InsightTableType
  group: InsightTableGroupBy
  customFiltersApplied?: boolean
  emptyStateProps: EmptyStateProps
  formattedState: TableDataVariables
  defaultGroupBy: InsightTableGroupBy
  setIsEmpty?: (isEmpty: boolean) => void
  customFilterTracker?: Record<string, boolean>
  useDataFetch: (variables: TableDataVariables) => TableProps
}

const PaginatedTable: React.FC<PaginatedTableProps> = ({
  type,
  group,
  title,
  search,
  setIsEmpty,
  useDataFetch,
  formattedState,
  defaultGroupBy,
  emptyStateProps,
  customFilterTracker,
  customFiltersApplied,
}) => {
  const { savedFilters } = useFilterProvider()

  const [currentPage, setCurrentPage] = useState<number>(0)
  const trackReportRun = useTrackReportRun()
  const currentRoute = useCurrentPage()
  const itemsPerPage = resolveItemsPerPage(type, group)
  const [lastCursor, setLastCursor] = useState<string | null | undefined>(null)
  const tableHeadRef = useRef<HTMLTableSectionElement>(null)

  const { hasComparisonDate } = useFinancialWorkspace()

  const resetPagination = useUnsafeCallback(() => {
    // Reset page to 1 when filters change
    setCurrentPage(0)
    setLastCursor(null)
  }, [])

  const variables = useUnsafeMemo(() => {
    return {
      ...formattedState,
      conceptIds: savedFilters.conceptIds || formattedState.conceptIds,
      locationIds: savedFilters.locationIds || formattedState.locationIds,
      costCenterIds: savedFilters.costCenterIds || formattedState.costCenterIds,
      legalEntityIds: savedFilters.selectedLegalEntityIds || formattedState.legalEntityIds,
      employeeIds: getEmployeeIds(savedFilters.selectedEmployees) || formattedState.employeeIds,
      search,
      itemsPerPage: itemsPerPage,
      after: lastCursor,
      groupBy: group,
      resetPagination,
    }
  }, [savedFilters, formattedState, search, group, lastCursor, itemsPerPage, resetPagination])

  useUnsafeEffect(() => {
    resetPagination()
  }, [JSON.stringify(formattedState), search, group, resetPagination])

  const isFirstRender = useRef(true)

  const hasFilters = useUnsafeMemo(() => {
    return !!(
      search ||
      customFiltersApplied ||
      savedFilters.conceptIds?.length ||
      formattedState.conceptIds?.length ||
      savedFilters.locationIds?.length ||
      formattedState.locationIds?.length ||
      savedFilters.costCenterIds?.length ||
      formattedState.costCenterIds?.length ||
      formattedState.formattedCompareToEndDate ||
      formattedState.formattedCompareToStartDate ||
      savedFilters.selectedLegalEntityIds?.length ||
      formattedState.legalEntityIds?.length ||
      getEmployeeIds(savedFilters.selectedEmployees)?.length ||
      formattedState.employeeIds?.length
    )
  }, [search, savedFilters, formattedState, customFiltersApplied])

  const { loading, items, headers, refetch, hasNextPage, endCursor, totalCount } =
    useDataFetch(variables)

  useSubscribe([REFETCH_DATA_EVENT], () => {
    refetch()
  })

  const reportRun = useUnsafeCallback(
    (value: boolean) => {
      const {
        period,
        conceptIds,
        locationIds,
        employeeIds,
        costCenterIds,
        legalEntityIds,
        formattedEndDate,
        formattedStartDate,
      } = formattedState

      trackReportRun(value, {
        group,
        period,
        search,
        formattedEndDate,
        formattedStartDate,
        page: currentRoute,
        customPayload: customFilterTracker,
        conceptIds: savedFilters.conceptIds || conceptIds,
        locationIds: savedFilters.locationIds || locationIds,
        costCenterIds: savedFilters.costCenterIds || costCenterIds,
        legalEntityIds: savedFilters.selectedLegalEntityIds || legalEntityIds,
        employeeIds: getEmployeeIds(savedFilters.selectedEmployees) || employeeIds,
      })
    },
    [group, search, currentRoute, savedFilters, trackReportRun, formattedState, customFilterTracker]
  )

  useVisitTracking({ loading, items })

  useUnsafeEffect(() => {
    reportRun(isFirstRender.current)
    isFirstRender.current = false
  }, [reportRun])

  useUnsafeEffect(() => {
    setIsEmpty?.(!items.length)
  }, [items, setIsEmpty])

  if (loading && !lastCursor) {
    return <Loading />
  }

  if (items.length === 0) {
    return <EmptyStateTable filtersApplied={hasFilters} emptyStateProps={emptyStateProps} />
  }

  const loadMore = (page: number) => {
    if (lastCursor !== endCursor && !loading) {
      setLastCursor(endCursor)
      setCurrentPage(page)
    }
  }

  return (
    <Table
      noTopRadius
      hideBorder
      autoWidth
      style={{
        borderTop: `1px solid ${colors.grey400}`,
      }}
    >
      <Table.Head ref={tableHeadRef}>
        <Header
          title={title}
          headers={headers}
          totalCount={totalCount}
          isGrouped={group !== defaultGroupBy}
        />
      </Table.Head>
      <Table.BodyWithInfiniteScroll
        page={currentPage}
        loadMore={loadMore}
        hasMore={hasNextPage}
        colSpan={headers.length + 1}
      >
        <NestedRows
          level={0}
          items={items}
          groupBy={group}
          tableHeadRef={tableHeadRef}
          currentPageCount={items.length}
          headerCount={headers.length / (hasComparisonDate ? 3 : 1)}
        />

        {!hasNextPage && <Ungrouped items={items} />}

        {loading &&
          Array.from({ length: variables.itemsPerPage ?? 0 }).map((_, index) => (
            <LoadingRow key={index} level={0} />
          ))}
      </Table.BodyWithInfiniteScroll>
      <Box height="s48" component="nav" aria-label={i18n.t('common.pagination.wrapper')}></Box>
    </Table>
  )
}

export default PaginatedTable
